import {useCallback, useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';
import {ResourceAllocationState, Role} from '../../../types';
import {removeRoleCascade} from '../cascadeDelete';

type AuthenticatedFetch = (
    url: string,
    options?: RequestInit
) => Promise<Response>;

interface SignalRConnection {
    getConnectionId(): string | null;
}

interface ActionDeps {
    // Ref, not the state value: callbacks read state via stateRef.current so
    // their identities survive state changes — the provider value can then be
    // memoized and downstream memo boundaries hold.
    stateRef: {current: ResourceAllocationState};
    setState: React.Dispatch<React.SetStateAction<ResourceAllocationState>>;
    authenticatedFetch: AuthenticatedFetch;
    signalRService: SignalRConnection;
    customerId: string | undefined;
    baseUrl: string;
}

export const useRoleActions = ({
    stateRef,
    setState,
    authenticatedFetch,
    signalRService,
    customerId,
    baseUrl
}: ActionDeps) => {
    const addRole = useCallback(
        async (role: Omit<Role, 'id'>): Promise<string> => {
            const id = uuidv4();
            const newRole = {...role, id};

            // Optimistic update
            setState(prev => ({
                ...prev,
                roles: {...prev.roles, [id]: newRole}
            }));

            // API call — awaited so callers can sequence dependent creates and
            // surface failures (the server persists this same client-generated id).
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                const revert = () =>
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.roles;
                        return {...prev, roles: rest};
                    });

                try {
                    const response = await authenticatedFetch(
                        `${baseUrl}/api/resource-allocation/roles`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-SignalR-Connection-Id': connectionId || ''
                            },
                            body: JSON.stringify({
                                id: id,
                                name: role.name,
                                description: role.description
                            })
                        }
                    );

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error('[addRole] API call failed:', errorText);
                        revert();
                        throw new Error(`Failed to create role: ${errorText}`);
                    }
                } catch (error: unknown) {
                    console.error('[addRole] API call error:', error);
                    revert();
                    throw error;
                }
            }

            return id;
        },
        [
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updateRole = useCallback(
        (id: string, updates: Partial<Role>) => {
            const state = stateRef.current;
            const previousRole = state.roles[id];

            // Optimistic update
            setState(prev => ({
                ...prev,
                roles: {
                    ...prev.roles,
                    [id]: {...prev.roles[id], ...updates}
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/roles/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(updates)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updateRole] API call failed:',
                                await response.text()
                            );
                            setState(prev => ({
                                ...prev,
                                roles: {...prev.roles, [id]: previousRole}
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[updateRole] API call error:', error);
                        setState(prev => ({
                            ...prev,
                            roles: {...prev.roles, [id]: previousRole}
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Expire a role (the default "delete" action): retired but kept for history
    // so past assignments still resolve. Patched in place, not removed.
    const expireRole = useCallback(
        (id: string) => {
            const state = stateRef.current;
            const previousRole = state.roles[id];
            if (!previousRole) return;
            const expiredOn = new Date().toISOString();

            setState(prev => ({
                ...prev,
                roles: {
                    ...prev.roles,
                    [id]: {...prev.roles[id], expiredOn}
                }
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    roles: {...prev.roles, [id]: previousRole}
                }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/roles/${id}`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[expireRole] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[expireRole] API call error:', error);
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Un-expire a role — clears expiredOn so it returns to active selection.
    const reinstateRole = useCallback(
        (id: string) => {
            const state = stateRef.current;
            const previousRole = state.roles[id];
            if (!previousRole) return;

            setState(prev => ({
                ...prev,
                roles: {
                    ...prev.roles,
                    [id]: {...prev.roles[id], expiredOn: null}
                }
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    roles: {...prev.roles, [id]: previousRole}
                }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/roles/${id}/reinstate`,
                    {
                        method: 'POST',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[reinstateRole] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[reinstateRole] API call error:', error);
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Permanently delete a role (created-in-error escape hatch). Rejects if the
    // server refuses (assignments reference it) so the caller can surface why.
    const permanentlyDeleteRole = useCallback(
        async (id: string): Promise<void> => {
            const state = stateRef.current;
            const backup = {
                role: state.roles[id],
                people: Object.fromEntries(
                    Object.entries(state.people).filter(([, p]) =>
                        p.roleIds?.includes(id)
                    )
                )
            };

            // Optimistic update — also scrub the role id from each person's roleIds.
            setState(prev => removeRoleCascade(prev, id));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    roles: {...prev.roles, [id]: backup.role},
                    people: {...prev.people, ...backup.people}
                }));

            if (!customerId || !authenticatedFetch) return;

            const connectionId = signalRService.getConnectionId();
            let response: Response;
            try {
                response = await authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/roles/${id}/permanent`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                );
            } catch (error: unknown) {
                console.error('[permanentlyDeleteRole] API call error:', error);
                revert();
                throw error;
            }

            if (!response.ok) {
                const message = await response.text();
                console.error(
                    '[permanentlyDeleteRole] API call failed:',
                    message
                );
                revert();
                throw new Error(message || 'Failed to permanently delete role');
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    return useMemo(
        () => ({
            addRole,
            updateRole,
            expireRole,
            reinstateRole,
            permanentlyDeleteRole
        }),
        [
            addRole,
            updateRole,
            expireRole,
            reinstateRole,
            permanentlyDeleteRole
        ]
    );
};
